import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import {
  FaArrowRight,
  FaClipboardCheck,
  FaDumbbell,
  FaLock,
  FaMoon,
  FaSun,
  FaUser,
  FaUsers,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useTheme } from '../../context/ThemeContext';
import { storeAdminSession } from '../../utils/adminSession';
import './LoginForm.css';

const loginHighlights = [
  {
    icon: FaClipboardCheck,
    title: 'Operasional multi-outlet',
    description: 'Kelola aktivitas harian outlet dari satu portal dengan struktur akses yang konsisten.',
  },
  {
    icon: FaUsers,
    title: 'Hak akses berbasis peran',
    description: 'Setiap akun masuk sesuai otorisasi yang telah ditetapkan untuk admin, outlet, atau karyawan.',
  },
  {
    icon: FaDumbbell,
    title: 'Akses terhubung ke sistem pusat',
    description: 'Proses autentikasi menggunakan layanan backend aktif tanpa mengubah alur kerja yang berjalan.',
  },
];

const authMetrics = [
  { label: 'Portal', value: 'Administrasi Outlet' },
  { label: 'Akses', value: 'Admin, outlet, karyawan' },
  { label: 'Status', value: 'Terhubung ke sistem' },
];

function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const redirectTarget = useMemo(() => {
    const from = location.state?.from;
    if (!from || typeof from.pathname !== 'string' || !from.pathname.startsWith('/')) {
      return '/dashboardadmin';
    }

    const search = typeof from.search === 'string' ? from.search : '';
    const hash = typeof from.hash === 'string' ? from.hash : '';
    return `${from.pathname}${search}${hash}`;
  }, [location.state]);

  const handleSubmit = async event => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}loginadmin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.error === '0') {
        storeAdminSession({
          token: data.token,
          user: data.user,
        });

        await Swal.fire({
          icon: 'success',
          title: 'Login berhasil',
          text: redirectTarget === '/dashboardadmin'
            ? 'Anda akan diarahkan ke dashboard.'
            : 'Anda akan diarahkan ke halaman yang diminta.',
          timer: 1300,
          showConfirmButton: false,
        });

        navigate(redirectTarget, { replace: true });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Login gagal',
          text: 'Username atau password tidak sesuai.',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Koneksi bermasalah',
        text: 'Tidak dapat terhubung ke server. Silakan coba kembali dalam beberapa saat.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <button type="button" className="auth-theme-toggle" onClick={toggleTheme}>
        {isDark ? <FaSun /> : <FaMoon />}
        <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
      </button>

      <Container className="auth-shell">
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col xl={10}>
            <div className="auth-layout">
              <section className="auth-side">
                <div className="auth-side__badge">
                  <FaDumbbell />
                  Portal Administrasi
                </div>

                <div className="auth-side__copy">
                  <h1>Akses terpusat untuk operasional outlet.</h1>
                  <p>
                    Masuk ke sistem untuk mengelola data operasional, member, transaksi,
                    dan aktivitas outlet sesuai hak akses akun Anda.
                  </p>
                </div>

                <div className="auth-side__metrics">
                  {authMetrics.map(item => (
                    <div key={item.label} className="auth-side__metric">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>

                <div className="auth-side__list">
                  {loginHighlights.map(item => {
                    const Icon = item.icon;

                    return (
                      <div key={item.title} className="auth-side__item">
                        <span className="auth-side__item-icon">
                          <Icon />
                        </span>
                        <div>
                          <strong>{item.title}</strong>
                          <p>{item.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="auth-side__footer">
                  <span>Multi-outlet</span>
                  <span>Role-based access</span>
                  <span>Secure session</span>
                </div>
              </section>

              <Card className="auth-card">
                <Card.Body>
                  <div className="auth-card__header">
                    <div className="logo-container">
                      <FaDumbbell className="logo-icon" />
                    </div>
                    <div>
                      <h2 className="auth-title">Masuk ke portal</h2>
                      <p>Gunakan akun yang telah terdaftar untuk melanjutkan ke dashboard administrasi.</p>
                    </div>
                  </div>

                  <Form onSubmit={handleSubmit} className="login-form">
                    <Form.Group className="form-group auth-field">
                      <Form.Label className="auth-field-label">Username</Form.Label>
                      <div className="custom-input-group">
                        <div className="input-icon">
                          <FaUser />
                        </div>
                        <Form.Control
                          type="text"
                          placeholder="Masukkan username"
                          autoComplete="username"
                          value={username}
                          onChange={event => setUsername(event.target.value)}
                          required
                          className="custom-input"
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="form-group auth-field">
                      <Form.Label className="auth-field-label">Password</Form.Label>
                      <div className="custom-input-group">
                        <div className="input-icon">
                          <FaLock />
                        </div>
                        <Form.Control
                          type="password"
                          placeholder="Masukkan password"
                          autoComplete="current-password"
                          value={password}
                          onChange={event => setPassword(event.target.value)}
                          required
                          className="custom-input"
                        />
                      </div>
                    </Form.Group>

                    <div className="auth-note">
                      Akun admin, outlet, dan karyawan dapat digunakan sesuai hak akses yang telah ditetapkan.
                    </div>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 auth-button"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                          <span>Memverifikasi...</span>
                        </>
                      ) : (
                        <>
                          <span>Masuk</span>
                          <FaArrowRight />
                        </>
                      )}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default LoginForm;
